import React from 'react';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  BarChart,
  Cards,
  StatusIndicator,
  Table
} from '@cloudscape-design/components';

// Dummy data for weekly metrics
const weeklyTrendData = {
  filesAnalyzed: [
    { week: 'Week 1', value: 45 },
    { week: 'Week 2', value: 62 },
    { week: 'Week 3', value: 58 },
    { week: 'Week 4', value: 69 }
  ],
  domainsIdentified: [
    { week: 'Week 1', value: 3 },
    { week: 'Week 2', value: 4 },
    { week: 'Week 3', value: 3 },
    { week: 'Week 4', value: 2 }
  ],
  microservicesGenerated: [
    { week: 'Week 1', value: 2 },
    { week: 'Week 2', value: 3 },
    { week: 'Week 3', value: 2 },
    { week: 'Week 4', value: 1 }
  ]
};

// Dummy data for team members productivity
const teamProductivity = [
  {
    member: 'Team Alpha',
    filesAnalyzed: 85,
    domainsIdentified: 5,
    microservicesGenerated: 3,
    status: 'success'
  },
  {
    member: 'Team Beta',
    filesAnalyzed: 72,
    domainsIdentified: 4,
    microservicesGenerated: 2,
    status: 'success'
  },
  {
    member: 'Team Gamma',
    filesAnalyzed: 45,
    domainsIdentified: 2,
    microservicesGenerated: 1,
    status: 'in-progress'
  }
];

// Current week summary
const currentWeekMetrics = {
  filesAnalyzed: 234,
  domainsIdentified: 12,
  microservicesGenerated: 8,
  completionRate: 94
};

const TeamProductivityView = () => {
  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Overview of team productivity metrics and trends"
        >
          Team Productivity
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Current Week Summary Cards */}
        <ColumnLayout columns={4} variant="text-grid">
          <Box
            padding="s"
            textAlign="center"
            variant="awsui-key-label"
          >
            <SpaceBetween size="xs">
              <Box variant="h3">{currentWeekMetrics.filesAnalyzed}</Box>
              <Box>Files Analyzed</Box>
            </SpaceBetween>
          </Box>
          <Box
            padding="s"
            textAlign="center"
            variant="awsui-key-label"
          >
            <SpaceBetween size="xs">
              <Box variant="h3">{currentWeekMetrics.domainsIdentified}</Box>
              <Box>Domains Identified</Box>
            </SpaceBetween>
          </Box>
          <Box
            padding="s"
            textAlign="center"
            variant="awsui-key-label"
          >
            <SpaceBetween size="xs">
              <Box variant="h3">{currentWeekMetrics.microservicesGenerated}</Box>
              <Box>Microservices Generated</Box>
            </SpaceBetween>
          </Box>
          <Box
            padding="s"
            textAlign="center"
            variant="awsui-key-label"
          >
            <SpaceBetween size="xs">
              <Box variant="h3">{currentWeekMetrics.completionRate}%</Box>
              <Box>Completion Rate</Box>
            </SpaceBetween>
          </Box>
        </ColumnLayout>

        {/* Weekly Trend Chart */}
        <Container
          header={
            <Header variant="h3">Weekly Trends</Header>
          }
        >
          <BarChart
            series={[
              {
                title: 'Files Analyzed',
                type: 'bar',
                data: weeklyTrendData.filesAnalyzed.map(item => ({
                  x: item.week,
                  y: item.value
                })),
                valueFormatter: value => `${value} files`
              }
            ]}
            xDomain={weeklyTrendData.filesAnalyzed.map(item => item.week)}
            yDomain={[0, 100]}
            hideFilter
            horizontalBars
            height={200}
          />
        </Container>

        {/* Team Performance Table */}
        <Table
          columnDefinitions={[
            {
              id: 'member',
              header: 'Team',
              cell: item => (
                <Box>
                  <StatusIndicator type={item.status}>{item.member}</StatusIndicator>
                </Box>
              ),
              sortingField: 'member'
            },
            {
              id: 'filesAnalyzed',
              header: 'Files Analyzed',
              cell: item => item.filesAnalyzed,
              sortingField: 'filesAnalyzed'
            },
            {
              id: 'domainsIdentified',
              header: 'Domains Identified',
              cell: item => item.domainsIdentified,
              sortingField: 'domainsIdentified'
            },
            {
              id: 'microservicesGenerated',
              header: 'Microservices Generated',
              cell: item => item.microservicesGenerated,
              sortingField: 'microservicesGenerated'
            }
          ]}
          items={teamProductivity}
          sortingColumn={{ sortingField: 'filesAnalyzed', sortingDescending: true }}
          header={
            <Header
              variant="h3"
              counter={`(${teamProductivity.length})`}
            >
              Team Performance
            </Header>
          }
          variant="container"
          stickyHeader
          stripedRows
        />

        {/* Productivity Insights */}
        <Cards
          cardDefinition={{
            header: item => (
              <Box variant="h4">
                {item.week}
              </Box>
            ),
            sections: [
              {
                content: item => (
                  <ColumnLayout columns={2}>
                    <div>
                      <Box variant="small">Domains Identified</Box>
                      <Box variant="h3">{item.value}</Box>
                    </div>
                    <div>
                      <Box variant="small">Success Rate</Box>
                      <Box variant="h3">{Math.round(item.value / 5 * 100)}%</Box>
                    </div>
                  </ColumnLayout>
                )
              }
            ]
          }}
          items={weeklyTrendData.domainsIdentified}
          header={
            <Header
              variant="h3"
            >
              Weekly Domain Analysis Insights
            </Header>
          }
        />
      </SpaceBetween>
    </Container>
  );
};

export default TeamProductivityView;
